import React, { useState, useRef } from 'react';

import { DateTime } from 'luxon';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { useClassStudentsQuery } from '@school_ify/schoolify-repository/src/teacher/types';

import useGrading from 'application/teacher/dashboard/assignments/useGrading';

import styles from './PeriodsTable.module.scss';
import TableRow from './TableRow/TableRow';
import AttendanceModal from '../components/AttendanceModal/AttendanceModal';
import { ItemI } from '../Attendance';

interface PeriodsTableProps {
  attendances?: ItemI[];
  subject?: any;
  dates: string[];
  refetchAttendances: () => void;
  classTitle: string;
  semesterId: string;
  currentDate: string;
  classId: string;
}

const PeriodsTable: React.FC<PeriodsTableProps> = ({
  subject,
  dates,
  attendances,
  refetchAttendances,
  semesterId,
  classId,
  classTitle,
  currentDate,
}) => {
  const [popoverOpened, setPopoverOpened] = useState('');

  const [modal, toggleModal] = useState(false);
  const [date, setDate] = useState('');
  const [scrolledToToday, setScrolledToToday] = useState(false);
  const scrollContainer = useRef(null);
  const { t } = useTranslation();
  const { addStudentGrade } = useGrading();

  const handleTableScroll = () => {
    if (popoverOpened !== '') {
      setPopoverOpened('');
    }
  };

  const setRef = element => {
    if (!scrolledToToday && element) {
      element.scrollIntoView({ inline: 'center' });
      setScrolledToToday(true);
    }
  };

  const { data: studentsData } = useClassStudentsQuery({
    variables: {
      class_id: classId,
    },
  });

  const students = (studentsData?.student_class || [])
    .filter(i => i.user.length)
    .map(u => u.user[0]);

  const onHeaderClick = str => {
    setDate(str);
    toggleModal(true);
  };
  return (
    <>
      {modal && (
        <AttendanceModal
          semesterId={semesterId}
          refetchAttendances={refetchAttendances}
          attendances={attendances}
          date={date}
          classTitle={classTitle}
          subjectId={subject.teaching_structure_id}
          subjectTitle={subject?.teaching_structure?.title || ''}
          classId={classId}
          openModal={modal}
          handleModal={() => {
            toggleModal(!modal);
          }}
          onInputChange={(id, mark) => addStudentGrade(id, mark)}
        />
      )}

      <div key={subject} className={styles.gradePeriods}>
        <div className={styles.subjectTitle}>{subject && subject.teaching_structure?.title}</div>
        {students?.length ? (
          <div ref={scrollContainer} onScroll={handleTableScroll} className={styles.tableWrapper}>
            <table>
              <thead>
                <tr>
                  <th></th>
                  {dates.map(date => {
                    const dateTitle = DateTime.fromFormat(date, 'y-MM-dd')
                      .setLocale(i18n.language)
                      .toFormat('dd.MM');
                    const isToday = date === currentDate;
                    return (
                      <th
                        ref={isToday ? setRef : null}
                        key={`${date}_dates`}
                        style={{
                          backgroundColor: isToday ? '#213960' : '',
                          color: isToday ? 'white' : '',
                        }}
                        className={classNames({ todaysCell: isToday })}
                      >
                        <div
                          tabIndex={1}
                          onClick={() => onHeaderClick(date)}
                          className={styles.cursorPointer}
                        >
                          {dateTitle}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {students.map(student => {
                  return (
                    <TableRow
                      classTitle={classTitle}
                      subjectTitle={subject?.teaching_structure?.title || ''}
                      popoverOpened={popoverOpened}
                      setPopoverOpened={setPopoverOpened}
                      key={`${student.id}_row`}
                      student={student}
                      subject={subject}
                      attendances={attendances}
                      refetchAttendances={refetchAttendances}
                      dates={dates}
                      classId={classId}
                      semesterId={semesterId}
                      subjectId={subject.teaching_structure_id}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.assignmentsMissing}>{t('attendance:noItemsFound')}</div>
        )}
      </div>
    </>
  );
};

export default React.memo(PeriodsTable);
